import {createSelector} from '@reduxjs/toolkit'
import {QueryStatus} from '@reduxjs/toolkit/query'
import {
  getPaymentSessionsLink,
  getPaymentSettingsLink
} from 'business/bofhLinks'
import {
  getEnabledWallets,
  getHttpRequestId,
  getIsEmbeddedWithKiwi,
  getPayrailsStatus
} from 'modules/common/selectors'
import {
  getDevicePlatform,
  getIsMobileDevice,
  getUserCountryWithFallback
} from 'modules/meta/selectors'
import {
  getExistingBookingOfferCheck,
  getIsChargeLaterEligible,
  getMainOfferCheck,
  getOfferCheckMerchantOfRecord,
  getPaymentFlowType,
  getUnifiedOfferCheck
} from 'modules/offerCheck/selectors'
import {PaymentFlowType} from 'modules/offerCheck/types'
import {PaymentMethodConfiguration} from 'services/payment/paymentMethods'
import {
  isAlternativePaymentMethod,
  isWalletPaymentMethod,
  rankPaymentMethods
} from 'utils/paymentMethodsHelpers'

// eslint-disable-next-line @vio/custom-rules/prefer-usebrand-hook -- composed into a selector, hooks are not available here
import {getBrandName} from '@vio/core/src/_web/brand/modules/selectors'
import {isFlutterAppWebView} from '@vio/core/src/_web/utils/flutter/isFlutterAppWebView'
import {PaymentMethodType} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'
import {getBovioApiKey} from '@vio/core/src/experiments/modules/selectors'
import {defaultCountry} from '@vio/core/src/localization/business/countries'
import isFhMor from '@vio/core/src/offer/business/isFhMor'
import {hasPayAtPropertyPayment} from '@vio/core/src/offer/business/offerPaymentMode'
import {paymentSettingsApi} from '@vio/core/src/offer/services/paymentSettingsApi'
import {
  PaymentMethodIntegration,
  PaymentSettings
} from '@vio/core/src/offer/types/paymentSettings'
import {getPriceDetails} from '@vio/core/src/price/business/price'

import type {PaymentRequestOptions} from '@stripe/stripe-js'
import type {Card} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'
import type {RootState} from 'store'

const getStateParam = (state: RootState) => state

// [PICC] Deliberately NOT derived from `getUnifiedOfferCheck`: that
// selector follows the card-currency (PICC) offer check once the card's
// currency is selected, and that response carries its own `links`, so the
// payment links would change on toggle.
const getPaymentLinks = (state: RootState) => {
  const {data} =
    getPaymentFlowType(state) === PaymentFlowType.ExistingBooking
      ? getExistingBookingOfferCheck(state)
      : getMainOfferCheck(state)

  return data?.links
}

const getPaymentSettingsUrl = (state: RootState) =>
  getPaymentSettingsLink(getPaymentLinks(state))?.href || ''

export const getPaymentSessionsActionLink = createSelector(
  [getPaymentLinks],
  links => getPaymentSessionsLink(links)
)

export const getPaymentSettingsParams = createSelector(
  [getBovioApiKey, getPaymentSettingsUrl, getHttpRequestId],
  (profileKey, url, httpRequestId) => ({
    url,
    profileKey,
    httpRequestId,
    clientUserAgent: `checkout/${process.env.NEXT_PUBLIC_APP_VERSION}`
  })
)

const getPaymentSettingsSelector = createSelector(
  [getPaymentSettingsParams],
  params => paymentSettingsApi.endpoints.getPaymentSettings.select(params)
)

const getPaymentSettingsResult = createSelector(
  [getStateParam, getPaymentSettingsSelector],
  (state: RootState, paymentSettingsSelector) => paymentSettingsSelector(state)
)

export const getPaymentMethods = createSelector(
  [getPaymentSettingsResult],
  ({data}): PaymentSettings['paymentMethods'] => data?.paymentMethods || []
)

export const getSupportedCards = createSelector(
  [getPaymentMethods],
  (paymentMethods): Card[] =>
    paymentMethods?.flatMap(method => method.cards) || []
)

export const getIsCardOnPayrails = createSelector(
  [getPaymentSettingsResult],
  ({data}): boolean =>
    Boolean(
      data?.paymentMethods.some(
        ({type, integration}) =>
          type === PaymentMethodType.Card &&
          integration === PaymentMethodIntegration.Payrails
      )
    )
)

export const getAvailablePaymentMethodTypes = createSelector(
  [getPaymentMethods],
  (paymentMethods): PaymentMethodType[] =>
    paymentMethods.map(method => method.type)
)

export const getIsPaymentSettingsLoading = createSelector(
  [getPaymentSettingsResult],
  result =>
    // Keep unfulfilled flow in loading to prevent booking errors
    // Improvements possible to properly handle errors
    result.status !== QueryStatus.fulfilled
)

/** Holds payment methods until every PSP they depend on is set up */
export const getIsPaymentMethodsLoading = createSelector(
  [
    getIsPaymentSettingsLoading,
    getOfferCheckMerchantOfRecord,
    getEnabledWallets,
    getIsCardOnPayrails,
    getPayrailsStatus
  ],
  (
    isPaymentSettingsLoading,
    merchantOfRecord,
    enabledWallets,
    isCardOnPayrails,
    payrailsStatus
  ): boolean =>
    isPaymentSettingsLoading ||
    (isFhMor({merchantOfRecord}) && !enabledWallets) ||
    (isCardOnPayrails && payrailsStatus === 'idle')
)

export const getPaymentRequestOptions = createSelector(
  [
    getUserCountryWithFallback,
    getUnifiedOfferCheck,
    getDevicePlatform,
    getBrandName
  ],
  (
    country,
    {data: offerData},
    devicePlatform,
    brandName
  ): PaymentRequestOptions => {
    const {prices = []} = offerData || {}
    const isPayAtProperty = hasPayAtPropertyPayment(offerData)
    const {chargeableCurrencyCode, totalUpFrontChargeablePrice} =
      getPriceDetails(prices, isPayAtProperty)
    const disablePayerInfoCollection =
      isFlutterAppWebView() && devicePlatform === 'android'
    return {
      country,
      currency:
        chargeableCurrencyCode?.toLowerCase() ||
        defaultCountry.currencyCode.toLowerCase(),
      total: {
        amount: Math.round(totalUpFrontChargeablePrice * 100),
        label: brandName
      },
      ...(disablePayerInfoCollection
        ? {
            requestPayerEmail: false,
            requestPayerName: false
          }
        : {})
    }
  }
)

export const getAvailablePaymentMethods = createSelector(
  [
    getEnabledWallets,
    getPaymentMethods,
    getUserCountryWithFallback,
    getIsMobileDevice,
    getIsChargeLaterEligible,
    getPayrailsStatus
  ],
  (
    availableWallets,
    paymentMethods,
    userCountry,
    isMobile,
    isChargeLaterEligible,
    payrailsStatus
  ): PaymentMethodConfiguration[] => {
    const wallets = availableWallets || []

    const availableMethods = paymentMethods
      .filter(({type: paymentMethodType, integration}) => {
        if (isWalletPaymentMethod(paymentMethodType))
          return wallets.some(wallet => wallet.type === paymentMethodType)
        if (integration === PaymentMethodIntegration.Payrails)
          return payrailsStatus !== 'failed'

        return true
      })
      .map(({type}) => ({type}))

    return rankPaymentMethods(
      availableMethods,
      userCountry,
      isMobile,
      isChargeLaterEligible
    )
  }
)

export const getEnabledPaymentMethods = createSelector(
  [
    getAvailablePaymentMethods,
    getOfferCheckMerchantOfRecord,
    getIsEmbeddedWithKiwi
  ],
  (
    availablePaymentMethods,
    merchantOfRecord,
    isEmbeddedWithKiwi
  ): PaymentMethodConfiguration[] => {
    // Wallets are only offered when we are the merchant of record
    if (merchantOfRecord && !isFhMor({merchantOfRecord}))
      return availablePaymentMethods.filter(
        paymentMethod =>
          paymentMethod.type !== PaymentMethodType.ApplePay &&
          paymentMethod.type !== PaymentMethodType.GooglePay
      )

    return availablePaymentMethods.filter(paymentMethod => {
      // APMs are not supported for Kiwi embedded
      if (isAlternativePaymentMethod(paymentMethod.type))
        return !isEmbeddedWithKiwi

      return true
    })
  }
)

/**
 * The top ranked method is preselected.
 */
export const getPreselectedPaymentMethod = createSelector(
  [getEnabledPaymentMethods],
  (enabledPaymentMethods): PaymentMethodType =>
    enabledPaymentMethods[0]?.type || PaymentMethodType.Card
)

export const getEligiblePaymentMethodTypes = createSelector(
  [getEnabledPaymentMethods],
  (enabledPaymentMethods): PaymentMethodType[] =>
    enabledPaymentMethods.map(method => method.type)
)
